import { useCallback, useEffect, useMemo, useState } from 'react';
import type { MdsWorksheetAggregate } from 'src/services/documentService/mdsWorksheet';

/**
 * BRK-1854 / BRK-1855: client-only draft state for the worksheet editor
 * (locked decision on BRK-1853: no server-side drafts — edits live here until
 * Submit, and the unsaved-changes guards are the only thing between the user
 * and losing them).
 *
 * Drafts are keyed by `itemCode|mdsColumn` and carry both a value AND its
 * source. A manual dropdown edit is MANUAL; applying a value from a
 * gg_collected document (BRK-1855) is GG_COLLECTED and remembers the row id +
 * level so the submission can be attributed — the backend (BRK-1853) rejects a
 * GG_COLLECTED item whose value does not match the cited document's level, so
 * any later manual edit MUST revert the source to MANUAL (which `setValue`
 * does). A draft value of '' records an explicit clear (always MANUAL); an
 * item with no draft is untouched and is NOT submitted. Dirtiness is keyed on
 * the value differing from the saved worksheet, so re-selecting the saved
 * value (or applying a document whose value already matches) clears the draft.
 */

export type MdsDraftSource =
  | { type: 'MANUAL' }
  | {
      type: 'GG_COLLECTED';
      ggCollectedId: number;
      levelType: string;
      documentType: string | null;
      dateOfService: string | null;
    }
  | {
      type: 'MDS_COLLECTED';
      mdsItemCollectedId: number;
      formSubmissionUuid: string | null;
      documentType: string | null;
      dateOfService: string | null;
    };

type DraftEntry = { value: string; source: MdsDraftSource };

export type MdsDraftItem = {
  sectionCode: string;
  itemCode: string;
  mdsColumn: string;
  itemValue: string | null;
  sourceType: string;
  sourceGgCollectedId?: number;
  sourceLevelType?: string;
  sourceFormSubmissionUuid?: string | null;
};

/** the value + provenance to attribute when applying a gg_collected level */
export type MdsGgApply = {
  value: string;
  ggCollectedId: number;
  levelType: string;
  documentType: string | null;
  dateOfService: string | null;
};

/** BRK-1866: the value + provenance to attribute when applying an MDS K candidate */
export type MdsItemApply = {
  value: string;
  mdsItemCollectedId: number;
  formSubmissionUuid: string | null;
  documentType: string | null;
  dateOfService: string | null;
};

const key = (itemCode: string, mdsColumn: string) => `${itemCode}|${mdsColumn}`;

export function useMdsWorksheetEditor(aggregate: MdsWorksheetAggregate | null) {
  const [drafts, setDrafts] = useState<Record<string, DraftEntry>>({});

  const assessmentUuid = aggregate?.assessment?.assessmentUuid;

  // switching anchors (or reloading after create/submit) starts a fresh draft
  useEffect(() => {
    setDrafts({});
  }, [assessmentUuid]);

  // itemCode|mdsColumn -> { sectionCode, serverValue, canEdit }
  const serverIndex = useMemo(() => {
    const index: Record<string, { sectionCode: string; serverValue: string; canEdit: boolean }> =
      {};
    aggregate?.sections?.forEach((section) => {
      section.items.forEach((item) => {
        item.columns.forEach((column) => {
          index[key(item.itemCode, column.mdsColumn)] = {
            sectionCode: section.sectionCode,
            serverValue: column.currentValue ?? '',
            canEdit: section.canEdit,
          };
        });
      });
    });
    return index;
  }, [aggregate]);

  /** write a draft, or clear it when the value matches the saved worksheet */
  const putDraft = useCallback(
    (draftKey: string, value: string, source: MdsDraftSource) => {
      setDrafts((prev) => {
        const serverValue = serverIndex[draftKey]?.serverValue ?? '';
        if (value === serverValue) {
          if (!(draftKey in prev)) {
            return prev;
          }
          const next = { ...prev };
          delete next[draftKey];
          return next;
        }
        return { ...prev, [draftKey]: { value, source } };
      });
    },
    [serverIndex]
  );

  /** manual dropdown edit — always MANUAL (reverts a prior document attribution) */
  const setValue = useCallback(
    (itemCode: string, mdsColumn: string, value: string) => {
      putDraft(key(itemCode, mdsColumn), value, { type: 'MANUAL' });
    },
    [putDraft]
  );

  /** BRK-1855: apply a gg_collected document level, remembering its provenance */
  const applyGgValue = useCallback(
    (itemCode: string, mdsColumn: string, gg: MdsGgApply) => {
      putDraft(key(itemCode, mdsColumn), gg.value, {
        type: 'GG_COLLECTED',
        ggCollectedId: gg.ggCollectedId,
        levelType: gg.levelType,
        documentType: gg.documentType,
        dateOfService: gg.dateOfService,
      });
    },
    [putDraft]
  );

  /** BRK-1866: apply an mds_item_collected K candidate, remembering its provenance */
  const applyMdsValue = useCallback(
    (itemCode: string, mdsColumn: string, mds: MdsItemApply) => {
      putDraft(key(itemCode, mdsColumn), mds.value, {
        type: 'MDS_COLLECTED',
        mdsItemCollectedId: mds.mdsItemCollectedId,
        formSubmissionUuid: mds.formSubmissionUuid,
        documentType: mds.documentType,
        dateOfService: mds.dateOfService,
      });
    },
    [putDraft]
  );

  const discardAll = useCallback(() => {
    setDrafts({});
  }, []);

  /**
   * BRK-1856 load-previous: merge an older submission's values into the draft
   * set as MANUAL edits (the value is the point; re-attributing to the old
   * document is fragile — that row may be inactive now, which would 400 on
   * resubmit). Each value still collapses to "not dirty" when it equals the
   * saved worksheet. BRK-2163: only values the caller can actually submit are
   * loaded — items no longer on this worksheet (e.g. Reliant's hidden pain/falls
   * items in an older version) and items in sections the caller's role can only
   * view are skipped. Either would otherwise become a draft the backend rejects
   * (400 / 403), blocking the caller's own edits until Discard.
   */
  const applyValues = useCallback(
    (values: { itemCode: string; mdsColumn: string; itemValue: string | null }[]) => {
      setDrafts((prev) => {
        const next = { ...prev };
        values.forEach(({ itemCode, mdsColumn, itemValue }) => {
          const draftKey = key(itemCode, mdsColumn);
          if (!serverIndex[draftKey]?.canEdit) {
            return;
          }
          const value = itemValue ?? '';
          const serverValue = serverIndex[draftKey]?.serverValue ?? '';
          if (value === serverValue) {
            delete next[draftKey];
          } else {
            next[draftKey] = { value, source: { type: 'MANUAL' } };
          }
        });
        return next;
      });
    },
    [serverIndex]
  );

  /** the value the editor should display: draft wins over the saved value */
  const displayValue = useCallback(
    (itemCode: string, mdsColumn: string): string => {
      const draftKey = key(itemCode, mdsColumn);
      if (draftKey in drafts) {
        return drafts[draftKey].value;
      }
      return serverIndex[draftKey]?.serverValue ?? '';
    },
    [drafts, serverIndex]
  );

  const isItemDirty = useCallback(
    (itemCode: string, mdsColumn: string) => key(itemCode, mdsColumn) in drafts,
    [drafts]
  );

  /** the pending draft's source (for the row's provenance badge), or null */
  const getDraftSource = useCallback(
    (itemCode: string, mdsColumn: string): MdsDraftSource | null => {
      const draftKey = key(itemCode, mdsColumn);
      return draftKey in drafts ? drafts[draftKey].source : null;
    },
    [drafts]
  );

  const dirtyCount = Object.keys(drafts).length;

  /**
   * The BRK-1853 submission payload for the touched items only. '' → null is
   * an explicit clear and is always MANUAL; GG_COLLECTED provenance rides only
   * with a real value (the backend re-validates value == document level).
   */
  const buildSubmissionItems = useCallback((): MdsDraftItem[] => {
    return Object.entries(drafts).map(([draftKey, entry]) => {
      const [itemCode, mdsColumn] = draftKey.split('|');
      const base = {
        sectionCode: serverIndex[draftKey]?.sectionCode ?? '',
        itemCode,
        mdsColumn,
        itemValue: entry.value === '' ? null : entry.value,
      };
      if (entry.source.type === 'GG_COLLECTED' && entry.value !== '') {
        return {
          ...base,
          sourceType: 'GG_COLLECTED',
          sourceGgCollectedId: entry.source.ggCollectedId,
          sourceLevelType: entry.source.levelType,
        };
      }
      // BRK-1866: an applied K candidate cites its source document only; the
      // backend re-verifies value == document and snapshots the rest itself
      if (entry.source.type === 'MDS_COLLECTED' && entry.value !== '') {
        return {
          ...base,
          sourceType: 'MDS_COLLECTED',
          sourceFormSubmissionUuid: entry.source.formSubmissionUuid,
        };
      }
      return { ...base, sourceType: 'MANUAL' };
    });
  }, [drafts, serverIndex]);

  return {
    setValue,
    applyGgValue,
    applyMdsValue,
    displayValue,
    isItemDirty,
    getDraftSource,
    dirtyCount,
    isDirty: dirtyCount > 0,
    discardAll,
    applyValues,
    buildSubmissionItems,
  };
}
